
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { formatDate } from '../utils/dateUtils';
import { getToolIcon, getToolName, renderContentPreview } from '../utils/toolUtils';
import { ResponseDetail } from './ResponseDetail';

interface ResponsesCardsProps {
  filteredResponses: any[];
  selectedItems: string[];
  toggleItemSelection: (id: string) => void;
  handleDelete: (id: string) => void;
}

export const ResponsesCards: React.FC<ResponsesCardsProps> = ({
  filteredResponses,
  selectedItems,
  toggleItemSelection,
  handleDelete
}) => {
  return (
    <div className="space-y-4">
      {filteredResponses.map((item) => (
        <Card key={item.id} className={selectedItems.includes(item.id) ? "bg-muted/50" : ""}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedItems.includes(item.id)}
                  onCheckedChange={() => toggleItemSelection(item.id)}
                  aria-label="Select item"
                />
                {getToolIcon(item.tool_type)}
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 -mt-1">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this saved item. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => handleDelete(item.id)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <CardTitle className="text-base mt-2">{item.title}</CardTitle>
            <CardDescription className="text-xs">
              {formatDate(item.created_at)}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm">
            <p className="line-clamp-2">{renderContentPreview(item)}</p>
          </CardContent>
          <CardFooter>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">
                  View Details
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[90vh] overflow-y-auto bg-white">
                <SheetHeader>
                  <SheetTitle>{item.title}</SheetTitle>
                  <SheetDescription>
                    {getToolName(item.tool_type)} • {formatDate(item.created_at)}
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6">
                  <ResponseDetail item={item} />
                </div>
              </SheetContent>
            </Sheet>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};
